import { GoogleRatingLine } from "@/components/site/GoogleRating";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Phone, CalendarCheck, ShieldCheck, Users, MapPin, Star, Clock, Wrench,
  Droplets, CloudLightning, Home, Ruler, Layers, ArrowRight, BookOpen, Palette,
} from "lucide-react";
import { HERO_CREW, METAL_INSTALL, OWNER_SALES_TEAM, ResponsivePicture, type ResponsiveImage } from "@/components/site/images";
import {
  HERNANDO_GALVALUME_FINISHED,
  CRYSTAL_RIVER_BLUE_SHINGLE,
  HOMOSASSA_BLUE_METAL_IN_PROGRESS,
} from "@/components/site/project-images";
import { BUSINESS, SERVICES, CITIES, BLOG_POSTS } from "@/components/site/constants";
import { BBBSeal } from "@/components/site/BBBSeal";
import { canonical } from "@/components/site/site-config";
import { CTASection } from "@/components/site/CTASection";
import { LazyContactForm } from "@/components/site/LazyContactForm";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Roofing Company in Citrus County, FL | Destination Roofing Inc." },
      { name: "description", content: "Destination Roofing Inc. provides roof repair, roof replacement and roof inspections in Citrus County and Spring Hill, FL. Licensed, local and trusted." },
      { property: "og:title", content: "Roofing Company in Citrus County, FL | Destination Roofing Inc." },
      { property: "og:description", content: "Destination Roofing Inc. provides roof repair, roof replacement and roof inspections in Citrus County and Spring Hill, FL. Licensed, local and trusted." },
      { property: "og:url", content: canonical("/") },
      { property: "og:type", content: "website" },
      { property: "og:image", content: HERO_CREW.fallback },
    ],
    links: [
      { rel: "canonical", href: canonical("/") },
      {
        rel: "preload",
        as: "image",
        href: HERO_CREW.preloadHref,
        imageSrcSet: HERO_CREW.preloadImgSrcset,
        imageSizes: "100vw",
        fetchPriority: "high",
      } as unknown as { rel: string; href: string },
    ],
  }),
  component: HomePage,
});

function HomePage() {
  return (
    <>
      {/* HERO */}
      <section className="relative brushed-dark text-brand-foreground overflow-hidden">
        <div className="absolute inset-0">
          <ResponsivePicture
            image={HERO_CREW}
            alt="Destination Roofing Inc. crew and truck on a Citrus County, FL roofing job"
            sizes="100vw"
            className="absolute inset-0 w-full h-full"
            imgClassName="w-full h-full object-cover opacity-60"
            eager
            fetchpriority="high"
          />
          <div className="absolute inset-0 lightning-overlay" />
          <div className="absolute inset-0 bg-[conic-gradient(from_200deg_at_55%_30%,rgba(185,130,69,0.12),transparent_40%,rgba(255,240,210,0.08),transparent_60%)]" />
          <div className="absolute inset-x-0 top-0 h-[3px] tan-marble" />
          <div className="absolute inset-x-0 bottom-0 h-[2px] tan-marble opacity-70" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 py-16 md:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-black/70 border border-cta/70 text-xs font-bold uppercase tracking-widest text-cta">
              <Users className="size-3.5" /> Local · Family-Owned · Licensed & Insured
            </div>
            <h1 className="mt-5 text-4xl sm:text-5xl md:text-6xl font-display font-bold leading-[1.02] uppercase tracking-tight">
              Roof Repair & Roof Replacement in <span className="marble-underline">Citrus County, FL</span>
            </h1>
            <div className="mt-4"><GoogleRatingLine /></div>
            <p className="mt-8 text-lg md:text-xl text-brand-foreground/90 max-w-2xl">
              Destination Roofing Inc. is a licensed and insured roofing company serving Citrus County, Florida, including Beverly Hills, Inverness, Crystal River, Homosassa, Lecanto, and nearby Spring Hill. We provide professional roof replacement, roof repair, roof inspections, and new roof installation for shingle, metal, and flat roofing systems. As a local, family-owned roofing contractor, we focus on dependable service, quality workmanship, and competitive pricing.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a href={BUSINESS.phoneHref} className="btn-cta w-full sm:w-auto">
                <Phone className="size-5" /> Call Now {BUSINESS.phone}
              </a>
              <Link to="/contact" className="btn-outline-light w-full sm:w-auto">
                <CalendarCheck className="size-5" /> Free Roof Inspection
              </Link>
            </div>
            <div className="mt-7 flex flex-wrap gap-x-5 gap-y-2 text-sm text-brand-foreground/90">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="size-4 text-cta" /> {BUSINESS.license}</span>
              <span className="inline-flex items-center gap-1.5"><MapPin className="size-4 text-cta" /> Citrus County, FL</span>
              <span className="inline-flex items-center gap-1.5"><Star className="size-4 text-cta" /> Free Inspection & Estimate</span>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="section-dark border-y-2 border-cta/70">
        <div className="mx-auto max-w-7xl px-4 py-6 grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
          <TrustItem icon={<ShieldCheck className="size-5" />} label="Licensed & Insured" sub={BUSINESS.license} />
          <TrustItem icon={<Users className="size-5" />} label="Family-Owned" sub="Local Citrus County roofer" />
          <TrustItem icon={<Clock className="size-5" />} label="After-Hours Emergencies" sub="Response available" />
          <TrustItem icon={<Star className="size-5" />} label="Free Inspection & Estimate" sub="No pressure. No cost." />
          <div className="flex flex-col gap-1">
            <BBBSeal />
            <div>
              <div className="font-display font-bold uppercase tracking-wider text-sm text-brand-foreground">BBB Accredited Business</div>
              <div className="text-xs text-brand-foreground/80">BBB Business Review</div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="section-charcoal border-y border-white/5">
        <div className="mx-auto max-w-7xl px-4 py-14 md:py-20">
          <div className="grid gap-10 md:grid-cols-2 items-center">
            <div>
              <div className="text-sm font-semibold uppercase tracking-wider text-cta">Why Homeowners Choose Us</div>
              <h2 className="mt-2 text-3xl md:text-4xl font-display font-bold text-brand-foreground">Why Citrus County Homeowners Choose Destination Roofing</h2>
              <p className="mt-4 text-brand-foreground/90 text-lg">
                We live and work in Citrus County. When you call, you talk to a real person — not a call center. We give homeowners straight answers about whether they need a full <Link to="/roof-replacement" className="text-cta underline">roof replacement</Link>, a <Link to="/roof-repair" className="text-cta underline">repair</Link>, or just some maintenance.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  "Honest, no-pressure inspections — we tell you what your roof actually needs",
                  "Free Roof Inspection & Estimate on every job",
                  "Licensed and insured with the State of Florida",
                  "Atlas Certified Scotchgard™ Shingle Sales Specialist",
                  "Family-owned — we treat your home the way we'd treat our own",
                ].map(item => (
                  <li key={item} className="flex items-start gap-3 text-brand-foreground/90">
                    <span className="mt-1 size-2 rounded-full bg-cta shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-7 flex flex-wrap gap-3">
                <a href={BUSINESS.phoneHref} className="btn-cta"><Phone className="size-5" /> Call {BUSINESS.phone}</a>
                <Link to="/about" className="btn-outline-light">About Destination Roofing</Link>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-[var(--shadow-card-dark)] border border-white/10">
              <ResponsivePicture
                image={OWNER_SALES_TEAM}
                alt="Destination Roofing owner and sales team in Crystal River, Florida"
                sizes="(min-width: 768px) 50vw, 100vw"
                imgClassName="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="section-charcoal py-14 md:py-20 border-y border-black">
        <div className="mx-auto max-w-7xl px-4">
          <div className="max-w-2xl">
            <div className="text-sm font-bold uppercase tracking-widest text-cta">Our Services</div>
            <h2 className="mt-2 text-3xl md:text-4xl font-display font-bold">Roofing Services in Citrus County</h2>
            <p className="mt-3 text-brand-foreground/90 text-lg">From a small leak repair to a complete roof replacement, we handle the full range of residential and light-commercial roofing work.</p>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {SERVICES.slice(0, 8).map(s => (
              <Link key={s.slug} to={s.slug} className="group card-contractor hover:card-contractor-hover block">
                <div className="icon-tile mb-4">{serviceIcon(s.title)}</div>
                <h3 className="font-display font-bold text-lg text-brand-foreground uppercase tracking-wide">{s.title}</h3>
                <p className="text-sm text-brand-foreground/80 mt-2 leading-relaxed">{s.short}</p>
                <div className="mt-4 pt-4 border-t border-white/10 text-xs font-bold uppercase tracking-widest text-cta inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                  See {s.title} <ArrowRight className="size-4" />
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-6 text-center">
            <Link to="/services" className="text-cta font-semibold underline">See all roofing services →</Link>
          </div>
        </div>
      </section>

      {/* METAL INSTALL VISUAL + ATLAS COLOR LINK */}
      <section className="section-charcoal py-14 md:py-20 border-y border-white/5">
        <div className="mx-auto max-w-7xl px-4 grid gap-10 md:grid-cols-2 items-center">
          <div className="rounded-2xl overflow-hidden shadow-[var(--shadow-card-dark)] border border-white/10">
            <ResponsivePicture
              image={METAL_INSTALL}
              alt="Metal roof installation in progress by Destination Roofing in Citrus County, FL"
              sizes="(min-width: 768px) 50vw, 100vw"
              imgClassName="w-full h-auto"
            />
          </div>
          <div>
            <div className="text-sm font-semibold uppercase tracking-wider text-cta">Preview Your Roof</div>
            <h2 className="mt-2 text-3xl md:text-4xl font-display font-bold">Preview Atlas Shingle Colors on Your Home</h2>
            <p className="mt-4 text-brand-foreground/90 text-lg">
              Destination Roofing is an Atlas Certified Scotchgard™ Shingle Sales Specialist. Upload a photo of your home and preview different Atlas shingle colors before you commit to a new roof.
            </p>
            <div className="mt-6">
              <Link to="/atlas-shingle-colors" className="btn-cta">
                <Palette className="size-5" /> Open the Atlas Roof Color Visualizer
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEMS WE FIX */}
      <section className="section-charcoal py-14 md:py-20 border-y border-white/5">
        <div className="mx-auto max-w-7xl px-4">
          <div className="max-w-2xl">
            <div className="text-sm font-bold uppercase tracking-widest text-cta">Common Issues</div>
            <h2 className="mt-2 text-3xl md:text-4xl font-display font-bold text-brand-foreground">Common Roofing Problems We Fix</h2>
            <p className="mt-3 text-brand-foreground/90 text-lg">
              If you're dealing with any of these, give us a call. We handle <Link to="/roof-repair" className="text-cta underline">roof leak repair</Link>, <Link to="/storm-damage-roof-repair" className="text-cta underline">storm damage</Link>, and everything in between.
            </p>
          </div>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <ProblemCard icon={<Droplets className="size-5" />} title="Roof leaks" desc="Water stains on ceilings, drips during rain, active roof leak repair." />
            <ProblemCard icon={<Wrench className="size-5" />} title="Missing shingles" desc="Blown-off or missing shingles after wind or storms." />
            <ProblemCard icon={<CloudLightning className="size-5" />} title="Storm damage" desc="Hail, wind damage, and after-hours emergency response for Florida storms." />
            <ProblemCard icon={<Home className="size-5" />} title="Old roofs" desc="Roofs at end of life, curling shingles, granule loss." />
            <ProblemCard icon={<Ruler className="size-5" />} title="Soft spots or sagging" desc="Rotten decking, sagging areas, structural issues." />
            <ProblemCard icon={<Layers className="size-5" />} title="Metal roof issues" desc="Loose fasteners, panel damage, sealant failures." />
            <ProblemCard icon={<Droplets className="size-5" />} title="Flat roof leaks" desc="Ponding water, seam failures, membrane damage." />
            <ProblemCard icon={<Wrench className="size-5" />} title="Gutter & soffit issues" desc="Overflowing gutters, damaged soffit and fascia." />
          </div>
        </div>
      </section>

      {/* RECENT PROJECTS */}
      <section className="section-charcoal py-14 md:py-20 border-y border-white/5">
        <div className="mx-auto max-w-7xl px-4">
          <div className="max-w-2xl">
            <div className="text-sm font-bold uppercase tracking-widest text-cta">Recent Work</div>
            <h2 className="mt-2 text-3xl md:text-4xl font-display font-bold text-brand-foreground">Recent Roofing Projects in Citrus County</h2>
            <p className="mt-3 text-brand-foreground/90 text-lg">
              Real project photos from Destination Roofing Inc. jobs across Citrus County, Florida.
            </p>
          </div>
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            <ProjectCard
              to="/projects/galvalume-metal-roof-hernando-fl"
              image={HERNANDO_GALVALUME_FINISHED}
              alt="Completed Galvalume metal roof installed by Destination Roofing Inc. in Hernando, Florida"
              location="Hernando, FL"
              heading="Galvalume Metal Roof in Hernando"
              caption="Completed Galvalume metal-roof installation on a residential home in Hernando, Florida."
              roofType="Metal Roofing"
            />
            <ProjectCard
              to="/projects/blue-shingle-roof-crystal-river-fl"
              image={CRYSTAL_RIVER_BLUE_SHINGLE}
              alt="Completed blue architectural shingle roof by Destination Roofing Inc. in Crystal River, Florida"
              location="Crystal River, FL"
              heading="Blue Shingle Roof in Crystal River"
              caption="Completed architectural shingle roofing project in Crystal River, Florida."
              roofType="Shingle Roofing"
            />
            <ProjectCard
              to="/projects/blue-metal-roof-homosassa-fl"
              image={HOMOSASSA_BLUE_METAL_IN_PROGRESS}
              alt="Destination Roofing crew installing a blue metal roof in Homosassa, Florida"
              location="Homosassa, FL"
              heading="Blue Metal Roof Installation in Homosassa"
              caption="Destination Roofing crew installing a blue metal roofing system on a Homosassa home addition."
              roofType="Metal Roofing"
              status="Installation in Progress"
            />
          </div>
          <div className="mt-6 text-center">
            <Link to="/projects" className="text-cta font-semibold underline">See all roofing projects →</Link>{" "}<span className="text-brand-foreground/60">·</span>{" "}<Link to="/gallery" className="text-cta font-semibold underline">Photo gallery</Link>
          </div>
        </div>
      </section>

      {/* RECENT ROOFING ADVICE */}
      <section className="section-charcoal py-14 md:py-20 border-y border-white/5">

        <div className="mx-auto max-w-7xl px-4">
          <div className="max-w-2xl">
            <div className="text-sm font-bold uppercase tracking-widest text-cta inline-flex items-center gap-2">
              <BookOpen className="size-4" /> From the Blog
            </div>
            <h2 className="mt-2 text-3xl md:text-4xl font-display font-bold text-brand-foreground">Recent Roofing Advice</h2>
            <p className="mt-3 text-brand-foreground/90 text-lg">
              Written by {BUSINESS.owner}, owner of Destination Roofing — practical roofing advice for Citrus County homeowners.
            </p>
          </div>
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {BLOG_POSTS.map(p => (
              <Link key={p.slug} to={p.slug} className="group rounded-xl bg-charcoal border border-white/10 border-t-2 border-t-cta p-6 hover:border-t-tan-light hover:-translate-y-0.5 transition-all shadow-[var(--shadow-card-dark)]">
                <h3 className="font-display font-bold text-lg text-brand-foreground leading-snug group-hover:text-cta">
                  {p.title}
                </h3>
                <p className="text-sm text-brand-foreground/80 mt-3 leading-relaxed">{p.excerpt}</p>
                <div className="mt-4 text-xs font-bold uppercase tracking-widest text-cta">Read the article →</div>
              </Link>
            ))}
          </div>
          <div className="mt-6 text-center">
            <Link to="/blog" className="text-cta font-semibold underline">See all articles →</Link>
          </div>
        </div>
      </section>

      {/* OFFER STRIP */}
      <section className="tan-marble border-y-4 border-black">
        <div className="mx-auto max-w-7xl px-4 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <div className="font-display font-bold text-2xl md:text-3xl uppercase tracking-wide text-black drop-shadow-[0_1px_0_rgba(255,235,200,0.5)]">Free Roof Inspection & Estimate</div>
            <div className="text-black/80 mt-1 font-semibold">Honest, no-pressure assessment from a local family-owned roofer.</div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <a href={BUSINESS.phoneHref} className="inline-flex items-center justify-center gap-2 bg-black text-cta border-2 border-black font-display font-bold uppercase tracking-wider px-6 py-3 rounded-md min-h-12 shadow-[0_4px_0_#000]">
              <Phone className="size-5" /> Call {BUSINESS.phone}
            </a>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-black/85 text-cta border-2 border-black font-display font-bold uppercase tracking-wider px-6 py-3 rounded-md min-h-12">
              Request a Free Estimate
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICE AREAS */}
      <section className="section-charcoal py-14 md:py-20 border-y border-white/5">
        <div className="mx-auto max-w-7xl px-4">
          <div className="max-w-2xl">
            <div className="text-sm font-bold uppercase tracking-widest text-cta">Service Areas</div>
            <h2 className="mt-2 text-3xl md:text-4xl font-display font-bold text-brand-foreground">Roofing Areas We Serve</h2>
            <p className="mt-3 text-brand-foreground/90 text-lg">
              Serving Citrus County, FL and surrounding communities. See our full <Link to="/service-areas" className="text-cta underline">service-areas page</Link> for details.
            </p>
          </div>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {CITIES.map(c => (
              <Link key={c.slug} to={c.slug} className="group rounded-lg bg-charcoal text-brand-foreground border border-white/10 border-l-4 border-l-cta p-5 hover:border-l-tan-light hover:-translate-y-0.5 transition-all shadow-[var(--shadow-card-dark)] flex items-center justify-between">
                <div>
                  <h3 className="font-display font-bold text-xl uppercase tracking-wide">{c.city}, {c.state}</h3>
                  <div className="text-sm text-brand-foreground/80 mt-1">Roofing company in {c.city}</div>
                </div>
                <ArrowRight className="size-5 text-cta group-hover:translate-x-1 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT FORM */}
      <section id="contact" className="section-charcoal border-y border-white/5">
        <div className="mx-auto max-w-7xl px-4 py-14 md:py-20 grid gap-10 lg:grid-cols-2">
          <div>
            <div className="text-sm font-semibold uppercase tracking-wider text-cta">Get in touch</div>
            <h2 className="mt-2 text-3xl md:text-4xl font-display font-bold text-brand-foreground">Request a Free Roof Inspection in Citrus County</h2>
            <p className="mt-3 text-brand-foreground/90 text-lg">
              Tell us a little about your roof and we'll get back to you quickly. Prefer to talk? Call us at {BUSINESS.phone}.
            </p>
            <div className="mt-6 space-y-3 text-sm text-brand-foreground/90">
              <div className="flex items-center gap-2"><Phone className="size-4 text-cta" /> <a href={BUSINESS.phoneHref} className="font-semibold text-brand-foreground">{BUSINESS.phone}</a></div>
              <div className="flex items-center gap-2"><MapPin className="size-4 text-cta" /> {BUSINESS.serviceArea}</div>
              <div className="flex items-center gap-2"><ShieldCheck className="size-4 text-cta" /> {BUSINESS.license} · Licensed & Insured</div>
              <div className="flex items-center gap-2"><Clock className="size-4 text-cta" /> Mon–Sat 6:30 AM–5:00 PM · After-hours emergency response available</div>
            </div>
          </div>
          <LazyContactForm formName="homepage_inspection" />
        </div>
      </section>

      <CTASection />
    </>
  );
}

function TrustItem({ icon, label, sub }: { icon: React.ReactNode; label: string; sub: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="size-10 rounded-md bg-cta/15 text-cta border border-cta/40 grid place-items-center shrink-0">{icon}</div>
      <div>
        <div className="font-display font-bold uppercase tracking-wider text-sm text-brand-foreground">{label}</div>
        <div className="text-xs text-brand-foreground/80">{sub}</div>
      </div>
    </div>
  );
}

function ProblemCard({ icon, title, desc }: { icon: React.ReactNode; title: string; desc: string }) {
  return (
    <div className="rounded-lg bg-charcoal text-brand-foreground border border-white/10 border-t-2 border-t-cta p-5 hover:-translate-y-0.5 hover:border-t-tan-light transition-all shadow-[var(--shadow-card-dark)]">
      <div className="icon-tile mb-3">{icon}</div>
      <div className="font-display font-bold uppercase tracking-wide text-brand-foreground">{title}</div>
      <p className="text-sm text-brand-foreground/80 mt-1.5 leading-relaxed">{desc}</p>
    </div>
  );
}

function serviceIcon(title: string) {
  const map: Record<string, React.ReactNode> = {
    "Roof Replacement": <Home className="size-5" />,
    "Roof Repair": <Wrench className="size-5" />,
    "Metal Roofing": <Layers className="size-5" />,
    "Shingle Roofing": <Home className="size-5" />,
    "Flat Roofing": <Ruler className="size-5" />,
    "Gutters": <Droplets className="size-5" />,
    "Soffit": <Home className="size-5" />,
    "Third Nail Wind Mitigation": <ShieldCheck className="size-5" />,
    "Roof Inspection": <CalendarCheck className="size-5" />,
    "Storm Damage Roof Repair": <CloudLightning className="size-5" />,
    "Commercial Roofing": <Home className="size-5" />,
  };
  return map[title] ?? <Home className="size-5" />;
}

function ProjectCard({
  to, image, alt, location, heading, caption, roofType, status,
}: {
  to: string;
  image: ResponsiveImage;
  alt: string;
  location: string;
  heading: string;
  caption: string;
  roofType: string;
  status?: string;
}) {
  return (
    <Link to={to} className="group block rounded-xl overflow-hidden border border-white/10 bg-white/5 hover:bg-white/10 transition-colors">
      <div className="relative">
        <ResponsivePicture
          image={image}
          alt={alt}
          sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
          imgClassName="w-full aspect-[4/3] object-cover"
        />
        {status && (
          <span className="absolute top-3 left-3 rounded-full bg-cta text-cta-foreground text-xs font-bold px-3 py-1">
            {status}
          </span>
        )}
      </div>
      <div className="p-4">
        <div className="text-xs font-bold uppercase tracking-widest text-cta">{location} · {roofType}</div>
        <div className="mt-1 text-lg font-semibold text-brand-foreground">{heading}</div>
        <p className="mt-2 text-sm text-brand-foreground/80">{caption}</p>
      </div>
    </Link>
  );
}

